import { useEffect, useRef, type RefObject } from 'react';

/**
 * Fires `onImpression` once when `ref` first intersects the viewport.
 * No-op if `IntersectionObserver` is missing. `enabled: false` defers
 * observing (a `display: none` target never intersects).
 */
export function useImpression<T extends Element>(
  onImpression: () => void,
  { enabled = true }: { enabled?: boolean } = {}
): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const firedRef = useRef(false);
  const onImpressionRef = useRef(onImpression);
  onImpressionRef.current = onImpression;

  useEffect(() => {
    if (!enabled) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (!el || firedRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      if (firedRef.current) return;
      if (!entries.some((entry) => entry.isIntersecting)) return;
      firedRef.current = true;
      observer.disconnect();
      onImpressionRef.current();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  return ref;
}
